import React, { useState, useEffect } from 'react';
import { UserRole, UserData, CreateUserRequest } from '../types';
import Modal from './ui/Modal';
import ConfirmationModal from './ui/ConfirmationModal';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import { useTranslation } from '../hooks/useTranslation';
import { API_CONFIG } from '../config';

interface UserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserRole: string | null;
}

// Role hierarchy for access control
const roleHierarchy: Record<string, number> = {
  'Super Admin': 6,
  'Admin': 5,
  'Producer': 4,
  'Project Manager': 3,
  'Coordinator': 2,
  // 'Client': 1,  // Закомментировано - используем Demo как Client
  'Demo': 0  // Demo = Client (level 0, read-only for assigned projects)
};

// Get available roles that current user can assign
const getAvailableRoles = (currentRole: string | null): string[] => {
  if (!currentRole) return [];
  
  const allRoles = ['Demo', /* 'Client', */ 'Coordinator', 'Project Manager', 'Producer', 'Admin', 'Super Admin'];  // Client закомментирован - используем Demo
  const currentLevel = roleHierarchy[currentRole] || 0;
  
  return allRoles.filter(role => {
    const roleLevel = roleHierarchy[role] || 0;
    
    // Admin cannot create Super Admin
    if (currentRole === 'Admin' && role === 'Super Admin') {
      return false;
    }
    
    // Can only create roles at or below current level
    return roleLevel <= currentLevel;
  });
};

const UserManagementModal: React.FC<UserManagementModalProps> = ({
  isOpen,
  onClose,
  currentUserRole
}) => {
  const { t } = useTranslation();
  const availableRoles = getAvailableRoles(currentUserRole);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [passwordEdit, setPasswordEdit] = useState<{
    isOpen: boolean;
    userId: string;
    username: string;
    newPassword: string;
  }>({
    isOpen: false,
    userId: '',
    username: '',
    newPassword: ''
  });

  const [emailEdit, setEmailEdit] = useState<{
    isOpen: boolean;
    userId: string;
    username: string;
    currentEmail: string;
    newEmail: string;
  }>({
    isOpen: false,
    userId: '',
    username: '',
    currentEmail: '',
    newEmail: ''
  });
  
  // Create user form state
  const [newUser, setNewUser] = useState<CreateUserRequest>({
    username: '',
    password: '',
    role: 'Producer',
    email: ''
  });
  const [passwordMode, setPasswordMode] = useState<'manual' | 'generate'>('manual');
  
  // Delete user confirmation modal
  const [deleteUserModal, setDeleteUserModal] = useState<{ isOpen: boolean; userId: string; username: string }>({ isOpen: false, userId: '', username: '' });

  // Fetch users when modal opens
  useEffect(() => {
    if (isOpen && currentUserRole === 'Super Admin') {
      fetchUsers();
    }
  }, [isOpen, currentUserRole]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(API_CONFIG.AUTH.USERS, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter out telegram_bot from the list (additional safety check)
        setUsers(data.filter((user: any) => user.username !== 'telegram_bot' && !user.email?.includes('telegram_bot')));
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch users');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = async () => {
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/auth/generate-password?length=8`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNewUser(prev => ({ ...prev, password: data.password }));
      } else {
        setError('Failed to generate password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate username
    if (!newUser.username || !newUser.username.trim()) {
      setError('Username is required');
      setLoading(false);
      return;
    }

    // Validate email
    if (!newUser.email || !newUser.email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Validate password for manual mode
    if (passwordMode === 'manual' && (!newUser.password || !newUser.password.trim())) {
      setError('Password is required when using manual mode');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('spellbook_token');
      const requestBody: any = {
        username: newUser.username,
        role: newUser.role,
        email: newUser.email
      };
      
      // Only include password if manual mode is selected
      if (passwordMode === 'manual') {
        requestBody.password = newUser.password;
      }

      const response = await fetch(API_CONFIG.AUTH.USERS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        // Reset form and refresh users list
        setNewUser({
          username: '',
          password: '',
          role: 'Producer',
          email: ''
        });
        setPasswordMode('manual');
        setShowCreateForm(false);
        await fetchUsers();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create user');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (userId: string, username: string) => {
    setDeleteUserModal({ isOpen: true, userId, username });
  };
  
  const confirmDeleteUser = async () => {
    const { userId } = deleteUserModal;
    setDeleteUserModal({ isOpen: false, userId: '', username: '' });

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.AUTH.USERS}/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchUsers();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete user');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const handleEditPassword = (userId: string, username: string) => {
    // Закрываем форму создания пользователя
    setShowCreateForm(false);
    
    setPasswordEdit({
      isOpen: true,
      userId,
      username,
      newPassword: ''
    });
  };

  const handleUpdatePassword = async () => {
    if (!passwordEdit.newPassword.trim()) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`API_CONFIG.AUTH.USERS/${passwordEdit.userId}/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: passwordEdit.newPassword })
      });

      if (response.ok) {
        setPasswordEdit({ isOpen: false, userId: '', username: '', newPassword: '' });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPasswordEdit = () => {
    setPasswordEdit({ isOpen: false, userId: '', username: '', newPassword: '' });
    setError('');
  };

  const handleEditEmail = (userId: string, username: string, currentEmail: string) => {
    // Закрываем форму создания пользователя
    setShowCreateForm(false);
    
    setEmailEdit({
      isOpen: true,
      userId,
      username,
      currentEmail,
      newEmail: currentEmail
    });
  };

  const handleUpdateEmail = async () => {
    if (!emailEdit.newEmail.trim()) {
      setError('Email is required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailEdit.newEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.AUTH.USERS}/${emailEdit.userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: emailEdit.newEmail })
      });

      if (response.ok) {
        // Update the user in the local state
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === emailEdit.userId 
              ? { ...user, email: emailEdit.newEmail, username: emailEdit.newEmail.split('@')[0] }
              : user
          )
        );
        setEmailEdit({ isOpen: false, userId: '', username: '', currentEmail: '', newEmail: '' });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update email');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEmailEdit = () => {
    setEmailEdit({ isOpen: false, userId: '', username: '', currentEmail: '', newEmail: '' });
    setError('');
  };

  // Reset all forms when modal closes
  const handleModalClose = () => {
    setShowCreateForm(false);
    setPasswordEdit({ isOpen: false, userId: '', username: '', newPassword: '' });
    setEmailEdit({ isOpen: false, userId: '', username: '', currentEmail: '', newEmail: '' });
    setPasswordMode('manual');
    setError('');
    onClose();
  };

  // Only show for Super Admin
  if (currentUserRole !== 'Super Admin') {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={`👥 ${t('userManagement.title')}`}
      size="md"
    >
      <div className="space-y-6">
        {error && (
          <div className="bg-red-900 border border-red-700 text-red-100 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Create User Button */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-white">
            {t('userManagement.title')}
            {showCreateForm && <span className="text-green-400 ml-2">• {t('userManagement.createMode')}</span>}
            {passwordEdit.isOpen && <span className="text-yellow-400 ml-2">• {t('userManagement.editMode')}</span>}
            {emailEdit.isOpen && <span className="text-blue-400 ml-2">• {t('userManagement.editEmailMode')}</span>}
          </h3>
          <Button
            onClick={() => {
              // Закрываем редактирование пароля и email
              setPasswordEdit({ isOpen: false, userId: '', username: '', newPassword: '' });
              setEmailEdit({ isOpen: false, userId: '', username: '', currentEmail: '', newEmail: '' });
              setShowCreateForm(!showCreateForm);
            }}
            variant="primary"
            size="sm"
            disabled={loading}
          >
{showCreateForm ? `❌ ${t('userManagement.cancel')}` : `➕ ${t('userManagement.createUser')}`}
          </Button>
        </div>

        {/* Create User Form */}
        {showCreateForm && !passwordEdit.isOpen && !emailEdit.isOpen && (
          <div className="space-y-4">
            <h4 className="text-md font-semibold text-white">{t('userManagement.createUser')}</h4>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <Input
                label={`${t('userManagement.username')} *`}
                id="username"
                name="username"
                value={newUser.username}
                onChange={handleInputChange}
                placeholder={t('userManagement.usernamePlaceholder')}
                required
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
{t('userManagement.password')}
                </label>
                
                {/* Password mode selector */}
                <div className="flex gap-4 mb-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="passwordMode"
                      value="manual"
                      checked={passwordMode === 'manual'}
                      onChange={(e) => {
                        setPasswordMode('manual');
                        setNewUser({ ...newUser, password: '' });
                      }}
                      className="mr-2 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-gray-300">Ввести пароль вручную</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="passwordMode"
                      value="generate"
                      checked={passwordMode === 'generate'}
                      onChange={(e) => {
                        setPasswordMode('generate');
                        setNewUser({ ...newUser, password: '' });
                      }}
                      className="mr-2 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-gray-300">Сгенерировать временный пароль и отправить по email</span>
                  </label>
                </div>

                {/* Manual password input */}
                {passwordMode === 'manual' && (
                <div className="flex gap-2">
                  <Input
                    id="password"
                    name="password"
                    label=""
                    value={newUser.password}
                    onChange={handleInputChange}
                    placeholder="Введите пароль"
                    required
                  />
                  <Button
                    type="button"
                    onClick={generatePassword}
                    variant="secondary"
                    size="sm"
                    title={t('userManagement.generatePassword')}
                  >
                    🎲
                  </Button>
                </div>
                )}

                {/* Generate mode info */}
                {passwordMode === 'generate' && (
                  <div className="bg-blue-900/20 border border-blue-700 rounded-md p-3">
                    <p className="text-sm text-blue-300">
                      📧 Временный пароль будет автоматически сгенерирован и отправлен на указанный email адрес. 
                      Пользователю потребуется сменить пароль при первом входе в систему.
                    </p>
                  </div>
                )}
              </div>
              
              <Select
                label={t('userManagement.role')}
                id="role"
                name="role"
                value={newUser.role}
                onChange={handleInputChange}
                required
              >
                {availableRoles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </Select>
              
              <Input
                label="Email *"
                id="email"
                name="email"
                type="email"
                value={newUser.email}
                onChange={handleInputChange}
                placeholder="user@example.com"
                required
              />

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
                <Button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  variant="secondary"
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || !newUser.username || (passwordMode === 'manual' && !newUser.password)}
                >
{loading ? '...' : t('userManagement.createUser')}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Users List */}
        <div className="space-y-4">
          <h4 className="text-md font-semibold text-white">{t('userManagement.userList')}</h4>
          
          {loading && !showCreateForm ? (
            <div className="p-4 text-center text-gray-400">
{t('userManagement.loading')}
            </div>
          ) : (
            <div className="space-y-3">
              {users.length === 0 ? (
                <div className="p-4 text-center text-gray-400 bg-gray-800 rounded-lg">
{t('userManagement.noUsers')}
                </div>
              ) : (
                users.map((user) => (
                  <div key={user.id} className="p-4 bg-gray-800 rounded-lg border border-gray-700 flex items-center justify-between hover:bg-gray-700 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {user.username ? user.username.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <h5 className="font-medium text-white">{user.username || 'Неизвестный пользователь'}</h5>
                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              user.role === 'Super Admin' 
                                ? 'bg-red-900 text-red-100' 
                                : user.role === 'Admin'
                                ? 'bg-orange-900 text-orange-100'
                                : user.role === 'Project Manager'
                                ? 'bg-purple-900 text-purple-100'
                                : user.role === 'Coordinator'
                                ? 'bg-green-900 text-green-100'
                                : user.role === 'Client'
                                ? 'bg-teal-900 text-teal-100'
                                : user.role === 'Demo'
                                ? 'bg-gray-900 text-gray-100'
                                : 'bg-blue-900 text-blue-100'
                            }`}>
                              {user.role}
                            </span>
                            {user.email && <span>📧 {user.email}</span>}
                            <span>📅 {new Date(user.createdAt).toLocaleDateString('ru-RU')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleEditPassword(user.id, user.username || 'пользователя')}
                        variant="secondary"
                        size="sm"
                        disabled={loading}
                        title={t('userManagement.changePassword')}
                      >
                        🔑
                      </Button>
                      <Button
                        onClick={() => handleEditEmail(user.id, user.username || 'пользователя', user.email || '')}
                        variant="secondary"
                        size="sm"
                        disabled={loading}
                        title={t('userManagement.changeEmail')}
                      >
                        📧
                      </Button>
                      <Button
                        onClick={() => handleDeleteUser(user.id, user.username || 'пользователя')}
                        variant="danger"
                        size="sm"
                        disabled={loading}
                        title={t('userManagement.delete')}
                      >
                        🗑️
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Password Edit Form */}
        {passwordEdit.isOpen && !showCreateForm && !emailEdit.isOpen && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-md font-semibold text-white">🔑 {t('userManagement.changePasswordTitle')}</h4>
              <Button
                type="button"
                onClick={handleCancelPasswordEdit}
                variant="secondary"
                size="sm"
              >
❌ {t('userManagement.close')}
              </Button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-300">
{t('userManagement.changingPasswordFor')}: <strong>{passwordEdit.username}</strong>
              </p>
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-1">{t('userManagement.newPassword')}</label>
                <div className="flex gap-2">
                  <Input
                    id="newPassword"
                    type="text"
                    label=""
                    value={passwordEdit.newPassword}
                    onChange={(e) => setPasswordEdit(prev => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="Введите новый пароль"
                    required
                  />
                  <Button
                    type="button"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('spellbook_token');
                        const response = await fetch(`${API_CONFIG.API_URL}/auth/generate-password?length=8`, {
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          }
                        });
                        
                        if (response.ok) {
                          const data = await response.json();
                          setPasswordEdit(prev => ({ ...prev, newPassword: data.password }));
                        } else {
                          console.error('Failed to generate password:', response.status);
                          setError('Failed to generate password');
                        }
                      } catch (err) {
                        console.error('Failed to generate password:', err);
                        setError('Connection error while generating password');
                      }
                    }}
                    variant="secondary"
                    size="sm"
                    title={t('userManagement.generatePassword')}
                  >
                    🎲
                  </Button>
                  {passwordEdit.newPassword && (
                    <Button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(passwordEdit.newPassword).then(() => {
                          // Password copied to clipboard
                        }).catch(err => {
                          console.error('Failed to copy password:', err);
                        });
                      }}
                      variant="secondary"
                      size="sm"
                      title={t('userManagement.copyPassword')}
                    >
                      📋
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
                <Button
                  type="button"
                  onClick={handleCancelPasswordEdit}
                  variant="secondary"
                  disabled={loading}
                >
                  Отмена
                </Button>
                <Button
                  type="button"
                  onClick={handleUpdatePassword}
                  variant="primary"
                  disabled={loading || !passwordEdit.newPassword}
                >
{loading ? '...' : t('userManagement.updatePassword')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Email Edit Form */}
        {emailEdit.isOpen && !showCreateForm && !passwordEdit.isOpen && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-md font-semibold text-white">📧 {t('userManagement.changeEmailTitle')}</h4>
              <Button
                type="button"
                onClick={handleCancelEmailEdit}
                variant="secondary"
                size="sm"
              >
                ❌ {t('userManagement.close')}
              </Button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-300">
                {t('userManagement.changingEmailFor')}: <strong>{emailEdit.username}</strong>
              </p>
              <div>
                <label htmlFor="currentEmail" className="block text-sm font-medium text-gray-300 mb-1">{t('userManagement.currentEmail')}</label>
                <Input
                  id="currentEmail"
                  type="email"
                  label=""
                  value={emailEdit.currentEmail}
                  disabled
                  className="bg-gray-700 text-gray-400"
                />
              </div>
              <div>
                <label htmlFor="newEmail" className="block text-sm font-medium text-gray-300 mb-1">{t('userManagement.newEmail')}</label>
                <Input
                  id="newEmail"
                  type="email"
                  label=""
                  value={emailEdit.newEmail}
                  onChange={(e) => setEmailEdit(prev => ({ ...prev, newEmail: e.target.value }))}
                  placeholder={t('userManagement.newEmailPlaceholder')}
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={handleCancelEmailEdit}
                  variant="secondary"
                  disabled={loading}
                >
                  {t('userManagement.cancel')}
                </Button>
                <Button
                  type="button"
                  onClick={handleUpdateEmail}
                  variant="primary"
                  disabled={loading || !emailEdit.newEmail || emailEdit.newEmail === emailEdit.currentEmail}
                >
                  {loading ? '...' : t('userManagement.updateEmail')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="bg-blue-900 border border-blue-700 text-blue-100 px-4 py-3 rounded text-sm">
          <p><strong>💡 {t('userManagement.hint')}:</strong></p>
          <ul className="mt-2 space-y-1 list-disc list-inside">
            <li>{t('userManagement.hintUsername')}</li>
            <li>{t('userManagement.hintPassword')}</li>
            <li>{t('userManagement.hintEmail')}</li>
            <li>{t('userManagement.hintAdminOnly')}</li>
            <li>{t('userManagement.hintChangePassword')}</li>
            <li>{t('userManagement.hintChangeEmail')}</li>
          </ul>
        </div>
      </div>
      
      {/* Delete User Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteUserModal.isOpen}
        onClose={() => setDeleteUserModal({ isOpen: false, userId: '', username: '' })}
        onConfirm={confirmDeleteUser}
        title={t('userManagement.deleteUserTitle') || 'Удалить пользователя'}
        message={`${t('userManagement.deleteUserConfirm') || 'Вы уверены, что хотите удалить пользователя'} "${deleteUserModal.username}"?`}
        confirmText={t('common.delete') || 'Удалить'}
        cancelText={t('common.cancel') || 'Отмена'}
        type="danger"
      />
    </Modal>
  );
};

export default UserManagementModal;
